import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

import {
  AnalyticsEventNames,
  Categories,
  DataLayerEventName,
  pushToAdobeDataLayer,
} from '@app/services/adobeDataLayer';
import { SESSION_STORAGE_VARIABLES, sessionStorageManager } from '@app/services/storageManager';
import { StackLink, WebRoutes } from '@app/router/constants';
import { HeaderWithBackNavigation } from '@app/components/HeaderWithBackNavigation';
import { OrderError } from '@app/components/OrderError';
import { useDisplayDisconnectAlert, useNavigate } from '@app/hooks';
import { selectBillingAccount, selectHasFiber, selectIsPreinstall, selectUser } from '@app/redux';
import {
  disconnectViasatInternet,
  resetDisconnectInternet,
  selectDisconnectInternet,
  setCancelInFlightOrder,
  setHasCancelledDisconnectOrder,
  setHasCancelledInFlightOrder,
} from '@app/redux/disconnect';

import { DisconnectPageLayout } from '../../containers';
import { useDisplayCIFAlert } from '../../hooks';
import DisconnectionCart from '../DisconnectCart';
import LeftSection from './LeftSection';
import { hasEquipmentAlert } from '../../utils';

const DisconnectReview = ({ handleCancel, step }: { handleCancel: () => void; step: number }) => {
  const { t } = useTranslation(['Disconnect']);
  const dispatch = useDispatch();
  const { replace } = useNavigate();

  const { loading, error, success: hasDisconnected } = useSelector(selectDisconnectInternet);
  const billingAccount = useSelector(selectBillingAccount);

  const date = new Date(billingAccount.nextBillPeriodStartDate as string);
  const nextBillingCycleDate = formatISO(date);

  const displayDisconnectAlert = useDisplayDisconnectAlert();
  const displayCIFAlert = useDisplayCIFAlert();
  const hasPreinstall = useSelector(selectIsPreinstall);
  const hasFiber = useSelector(selectHasFiber);

  const { user } = useSelector(selectUser);

  const { indoorUnit } = user;
  const showEquipmentAlert = hasEquipmentAlert(indoorUnit, t) && !hasPreinstall;

  const hasCancelledInFlightSuccess = sessionStorageManager.getItem(SESSION_STORAGE_VARIABLES.CIF_ORDER_SUCCESS);

  const clearSession = () => {
    sessionStorageManager.setItem(SESSION_STORAGE_VARIABLES.CIF_ORDER_SUCCESS, false);
    dispatch(setHasCancelledDisconnectOrder(false));
    dispatch(setHasCancelledInFlightOrder(false));
    sessionStorageManager.removeItem(SESSION_STORAGE_VARIABLES.DISMISSED_ALERTS);
  };

  useEffect(() => {
    if (hasDisconnected) {
      if (hasCancelledInFlightSuccess) {
        replace(`/${WebRoutes.Home}`);
        displayCIFAlert();
      } else {
        sessionStorageManager.setItem(SESSION_STORAGE_VARIABLES.SHOW_PLAN_TAB, true);
        replace(`/${StackLink.Profile}`);
      }
      if (!hasPreinstall) {
        displayDisconnectAlert();
      }
    }
  }, [hasDisconnected, hasCancelledInFlightSuccess, hasPreinstall]);

  const handleContinue = () => {
    clearSession();
    dispatch(disconnectViasatInternet(nextBillingCycleDate));
    pushToAdobeDataLayer({
      event: DataLayerEventName.PAGE_VIEW,
      event_name: DataLayerEventName.PAGE_VIEW,
      event_label: `${AnalyticsEventNames.Disconnected}`,
      event_category: Categories.Disconnect,
    });
  };

  const handleCIFContinue = () => {
    clearSession();
    dispatch(setCancelInFlightOrder());
    pushToAdobeDataLayer({
      event: DataLayerEventName.PAGE_VIEW,
      event_name: DataLayerEventName.PAGE_VIEW,
      event_label: `${AnalyticsEventNames.CIFOrderInitiated}`,
      event_category: Categories.Disconnect,
    });
  };

  if (error) {
    return <OrderError title={t('Error.Title')} handlePrimaryAction={() => dispatch(resetDisconnectInternet())} />;
  }

  return (
    <DisconnectPageLayout
      step={step}
      header={
        <HeaderWithBackNavigation
          title={t('page.title.review')}
          goBackLabel={t('button.back')}
          handleBackNavigation={handleCancel}
        />
      }
      leftContent={
        <LeftSection
          handleContinue={hasPreinstall ? handleCIFContinue : handleContinue}
          handleCancel={handleCancel}
          isRemoveViasatLoading={loading}
          showEquipmentAlert={!!showEquipmentAlert && !hasFiber}
        />
      }
      rightContent={<DisconnectionCart />}
    />
  );
};

export default DisconnectReview;
